import { useState, useEffect, useCallback } from "react"
import { Container, Row, Col, Spinner, Card, Table, Form } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import API from "../../common/API"
import { parsedUser } from "../../common/GetCurrentUser"
import { isSuperAdmin } from "../../common/helpers"
import DateRangeFilter from "../../components/indicators/DateRangeFilter"
import IndicatorCard from "../../components/indicators/IndicatorCard"
import SuperAdminBackLink from "../../components/platform/SuperAdminBackLink"
import moment from "moment"
import { FleetSummary, VehicleVisitStats } from "../../types/Indicators.types"
import { Doughnut, Bar } from "react-chartjs-2"
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js"

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

const PIE_COLORS = ["#0d6efd", "#198754", "#dc3545", "#ffc107", "#6f42c1", "#fd7e14", "#20c997", "#0dcaf0", "#d63384", "#6c757d"]

interface OrganizationOption {
  id: string
  name: string
}

export default function GpsIndicatorsPage() {
  const { t } = useTranslation("global")
  const user: any = parsedUser()
  const userOrgId = user?.OrganizationId || user?.organizationId || ""
  const canSelectOrganization = isSuperAdmin(user)

  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(false)
  const [organizations, setOrganizations] = useState<OrganizationOption[]>([])
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(canSelectOrganization ? "" : userOrgId)
  const [fleet, setFleet] = useState<FleetSummary | null>(null)
  const [vehicleStats, setVehicleStats] = useState<VehicleVisitStats | null>(null)
  const effectiveOrgId = canSelectOrganization ? selectedOrganizationId : userOrgId

  const clearData = useCallback(() => {
    setFleet(null)
    setVehicleStats(null)
  }, [])

  const loadOrganizations = useCallback(async () => {
    if (!canSelectOrganization) return

    try {
      setIsLoadingOrganizations(true)
      const res = await API.getAction("Organization/AdminList", "Page=0&Quantity=200")
      if (res.data.status === "ok") {
        const records = res.data.response ?? []
        setOrganizations(records)
      }
    } catch (ex) {
      console.error(ex)
    } finally {
      setIsLoadingOrganizations(false)
    }
  }, [canSelectOrganization])

  const bindData = useCallback(async (startDate?: string, endDate?: string) => {
    if (!effectiveOrgId) return

    try {
      setIsLoading(true)
      const start = startDate || moment().startOf("month").format("YYYY-MM-DD")
      const end = endDate || moment().format("YYYY-MM-DD")

      const [fleetRes, vehicleRes] = await Promise.all([
        API.getAction("Indicators/FleetSummary", `organizationId=${effectiveOrgId}`),
        API.getAction("Indicators/VehicleVisitStats", `organizationId=${effectiveOrgId}&startDate=${start}&endDate=${end}`),
      ])

      if (fleetRes.data.status === "ok") setFleet(fleetRes.data.response)
      if (vehicleRes.data.status === "ok") setVehicleStats(vehicleRes.data.response)
    } catch (ex) {
      console.error(ex)
    } finally {
      setIsLoading(false)
    }
  }, [effectiveOrgId])

  useEffect(() => {
    loadOrganizations()
  }, [loadOrganizations])

  useEffect(() => {
    if (!effectiveOrgId) {
      clearData()
      return
    }

    bindData()
  }, [effectiveOrgId, bindData, clearData])

  const statusChart = fleet ? {
    labels: fleet.byStatus.map(s => s.name),
    datasets: [{
      data: fleet.byStatus.map(s => s.count),
      backgroundColor: PIE_COLORS.slice(0, fleet.byStatus.length),
    }]
  } : null

  const brandChart = fleet ? {
    labels: fleet.byBrand.map(b => b.name),
    datasets: [{
      label: t("Vehicles"),
      data: fleet.byBrand.map(b => b.count),
      backgroundColor: "#0d6efd",
      borderRadius: 4,
    }]
  } : null

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${Math.round(seconds)}s`
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`
    return `${(seconds / 3600).toFixed(1)}h`
  }

  return (
    <Container fluid className="px-4 py-3">
      <SuperAdminBackLink />

      <h4 className="mb-3">
        <i className="fas fa-satellite me-2 text-primary" />
        {t("Indicators")} - {t("GPS / Fleet")}
      </h4>

      {canSelectOrganization && (
        <Row className="g-2 align-items-end mb-3">
          <Col md={4} lg={3}>
            <Form.Label className="small text-muted mb-1">{t("Organization")}</Form.Label>
            <Form.Select
              size="sm"
              value={selectedOrganizationId}
              onChange={e => setSelectedOrganizationId(e.target.value)}
              disabled={isLoadingOrganizations}
            >
              {!selectedOrganizationId && <option value="">{t("Select Organization")}</option>}
              {organizations.map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </Form.Select>
          </Col>
        </Row>
      )}

      <DateRangeFilter onFilter={(s, e) => bindData(s, e)} loading={isLoading} />

      {canSelectOrganization && !selectedOrganizationId ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="py-4 text-center text-muted">
            {t("Select an organization to load indicators")}
          </Card.Body>
        </Card>
      ) : isLoading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : (
        <>
          <Row className="g-3 mb-4">
            <Col xs={6} md={3}>
              <IndicatorCard title={t("Total Vehicles")} value={fleet?.totalVehicles ?? 0} format="number" icon="fa-car" color="#0d6efd" />
            </Col>
            <Col xs={6} md={3}>
              <IndicatorCard title={t("Active Drivers")} value={fleet?.activeDrivers ?? 0} format="number" icon="fa-id-card" color="#198754" />
            </Col>
            <Col xs={6} md={3}>
              <IndicatorCard title={t("Sites Covered")} value={vehicleStats?.distinctSitesCovered ?? 0} format="number" icon="fa-map-marked-alt" color="#6f42c1" />
            </Col>
            <Col xs={6} md={3}>
              <IndicatorCard title={t("Avg Distance")} value={`${vehicleStats?.avgDistance?.toFixed(1) ?? 0} km`} icon="fa-road" color="#fd7e14" />
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col md={4}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Header className="bg-white border-bottom">
                  <h6 className="mb-0"><i className="fas fa-circle-half-stroke me-2 text-primary" />{t("Vehicles by Status")}</h6>
                </Card.Header>
                <Card.Body className="d-flex align-items-center justify-content-center">
                  {statusChart ? (
                    <div style={{ maxWidth: 250 }}>
                      <Doughnut data={statusChart} options={{ responsive: true, plugins: { legend: { position: "bottom" } } }} />
                    </div>
                  ) : <div className="text-muted">{t("No data available")}</div>}
                </Card.Body>
              </Card>
            </Col>
            <Col md={8}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Header className="bg-white border-bottom">
                  <h6 className="mb-0"><i className="fas fa-industry me-2 text-primary" />{t("Vehicles by Brand")}</h6>
                </Card.Header>
                <Card.Body>
                  {brandChart ? (
                    <Bar data={brandChart} options={{ responsive: true, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true } } }} />
                  ) : <div className="text-center text-muted py-4">{t("No data available")}</div>}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {vehicleStats && vehicleStats.perVehicle.length > 0 && (
            <Row className="g-3 mb-4">
              <Col xs={12}>
                <Card className="shadow-sm border-0">
                  <Card.Header className="bg-white border-bottom">
                    <h6 className="mb-0"><i className="fas fa-list me-2 text-primary" />{t("Vehicle Visit Details")}</h6>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <Table hover responsive className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="ps-3">{t("Vehicle")}</th>
                          <th className="text-center">{t("Visits")}</th>
                          <th className="text-center">{t("Visits > 5 min")}</th>
                          <th className="text-center">{t("Avg Distance")}</th>
                          <th className="text-center">{t("Avg Duration")}</th>
                          <th className="text-center pe-3">{t("Sites Covered")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vehicleStats.perVehicle.map((v, i) => (
                          <tr key={i}>
                            <td className="ps-3 fw-semibold">{v.vehicleName}</td>
                            <td className="text-center">{v.visitCount}</td>
                            <td className="text-center">{v.visitsOver5Min}</td>
                            <td className="text-center">{v.avgDistance.toFixed(1)} km</td>
                            <td className="text-center">{formatDuration(v.avgDurationSeconds)}</td>
                            <td className="text-center pe-3">{v.distinctSites}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </>
      )}
    </Container>
  )
}
